import { db, auth } from "../../firebase/firebase";
import {
  doc,
  getDoc,
  collection,
  getDocs,
  QueryDocumentSnapshot,
  query,
  where,
  limit,
  startAfter,
  and,
  QueryCompositeFilterConstraint,
  QueryNonFilterConstraint,
  QueryFieldFilterConstraint,
  orderBy,
  getCountFromServer,
  endBefore,
  limitToLast
} from "firebase/firestore";


import { CustomError } from "../../utils/customeError";
import { Pagination } from "../enums";
import { FeedbakType, SKFeedback } from "../../models/SKFeedback";


const getFeedback = async (id: string, type:"issue"|"suggestion") : Promise<SKFeedback> => {
  const subCollection = type === "issue" ? "issues" : "suggestions";
  let feedbackRef = doc(db, "feedbacks", "feedbackSummary", subCollection, id);
  const docSnap = await getDoc(feedbackRef);
  if (docSnap.exists()) {
    const {iOS, android, ...rest} = docSnap.data();
    // const oSVersion = iOS ?? android;
    return {
      id: docSnap.id,
      ...rest,
    }
  } else {
    throw new Error("Channel does not exist!");
  }
};


const getFeedbacksByIds = async (ids: string[]) : Promise<SKFeedback[]> => {
   
  const colRef = collection(db, "feedback");

  // Use "where" clause with "__name__" for document IDs
  const q = query(colRef, where("__name__", "in", ids));
  const querySnapshot = await getDocs(q);

  const documents = querySnapshot.docs.map((doc) => ({
    id: doc.id,
    ...doc.data(),
  } as SKFeedback)) ;

  return documents;
};



const applyPaginationQuery = (pageSize: number, moveTo?:Pagination, currentDoc?:QueryDocumentSnapshot): QueryNonFilterConstraint[] =>{

  let myNonFilterQuery: QueryNonFilterConstraint[] = []

  switch (moveTo) {
    case Pagination.Next:
      if (currentDoc) {
        myNonFilterQuery.push(startAfter(currentDoc))
      }
      myNonFilterQuery.push(limit(pageSize));
      break;

    case Pagination.Previous:
      if (currentDoc) {
        myNonFilterQuery.push(endBefore(currentDoc))
      }
      myNonFilterQuery.push(limitToLast(pageSize));
      break;

    case Pagination.First:
      myNonFilterQuery.push(limit(pageSize));
      break;

    case Pagination.Last:
      myNonFilterQuery.push(limitToLast(pageSize));
      break;

    default:
        myNonFilterQuery.push(limit(pageSize));
        break;
      
  }

  return myNonFilterQuery;

}


const fetchFeedback = async (
  type:FeedbakType,
  pageSize: number,
  lastDocument?: QueryDocumentSnapshot,
  moveTo?: Pagination,
  sortConfig?: { key: string; direction: "asc" | "desc" }
): Promise<{
  docs: SKFeedback[];
  firstDoc: QueryDocumentSnapshot | undefined;
  lastDoc: QueryDocumentSnapshot | undefined;
  hasNext: boolean;
  hasPrev: boolean;
}> => {

  const subCollectionPath = type === "issue" ? "issues" : "suggestions";
  const ref = collection(db, "feedbacks", "feedbackSummary", subCollectionPath);

  // Build filters
  // const constraints: QueryFieldFilterConstraint[] = [];
  // if (filterFields.length && filterValues.length) {
  //   for (let i = 0; i < filterFields.length; i++) {
  //     const fieldPath = filterFields[i];
  //     const fieldValue = filterValues[i];
  //     if (fieldPath && fieldValue !== undefined) {
  //       constraints.push(where(fieldPath, "==", fieldValue));
  //     }
  //   }
  // }

  // Pagination + Sorting
  const nonFilterConstraints = applyPaginationQuery(pageSize + 1, moveTo, lastDocument);
  const sortArray = sortConfig
    ? [orderBy(sortConfig.key, sortConfig.direction)]
    : [orderBy("feedbackDate", "desc")];

  const q = query(ref, ...sortArray, ...nonFilterConstraints);

  try {
    const snapshot = await getDocs(q);

    
    const docs = snapshot.docs.slice(0, pageSize).map((doc) => {
      const {iOS, android, ...rest} = doc.data();
      const oSVersion = iOS ?? android;
      return {
        id: doc.id,
        ...rest,
        oSVersion
      }
   }) as SKFeedback[];

    const firstDoc = snapshot.docs.length > 0 ? snapshot.docs[0] : undefined;
    const lastDoc =
      snapshot.docs.length > pageSize
        ? snapshot.docs[pageSize - 1]
        : moveTo === Pagination.Previous
        ? snapshot.docs[pageSize - 1]
        : undefined;

    let hasNext = snapshot.docs.length > pageSize;
    let hasPrev = !!lastDocument;

    if (moveTo === Pagination.First) hasPrev = false;
    if (moveTo === Pagination.Last) hasNext = false;

    return { docs, firstDoc, lastDoc, hasNext, hasPrev };
  } catch (err) {
    console.error("Error fetching feedback:", err);
    throw new CustomError("An error occurred while fetching data");
  }
};


 
const getFeedbackCounts = async (): Promise<{ [FeedbakType.issue]:number,[FeedbakType.suggestion]:number }> => {

  
  let feedbackRef = doc(db, "feedbacks", "feedbackSummary");

  const summarySnap = await getDoc(feedbackRef);

  try {
    // Execute all queries in parallel
    if (summarySnap.exists()) {
      const {issueCount, suggestionCount} = summarySnap.data();
      return {
        [FeedbakType.issue]: issueCount,
        [FeedbakType.suggestion]: suggestionCount,
      };
    } else {
      console.warn("No feedback summary document found.");
      return {
        [FeedbakType.issue]: 0,
        [FeedbakType.suggestion]: 0,
      };
    }
  } catch (error) {
    console.error("Error getting channel counts: ", error);
     return {
      [FeedbakType.issue]: 0,
      [FeedbakType.suggestion]: 0
    }; // Return 0 for all if there's an error
  }
}




export {
  fetchFeedback,
  getFeedbackCounts,
  getFeedbacksByIds,
  getFeedback
};